/**
 * Shared viewport sizing for the two tables on the Review Cycle Details tabs, so Review Subjects
 * and Review Forms size identically instead of one hugging its content and the other not.
 *
 * A table short enough to fit under the cap runs AG Grid in `autoHeight` layout, which sizes the
 * grid to its rows and never opens a scroll viewport. Pinning the wrapper to a pixel height
 * instead leaves the grid in its normal layout, and that always builds a scrollable body, so a
 * six-row table still got a scrollbar however exactly the math matched the rows (PY-14398).
 *
 * Past the cap the grid goes back to a fixed viewport that scrolls internally, which is what keeps
 * row virtualization alive on cycles with thousands of reviews. Virtualization is not worth losing
 * below the cap, where every row is on screen anyway.
 *
 * Each consumer passes both heights to its grid rather than leaving them to the shared table
 * theme, so this math cannot drift from what the grid actually renders.
 */
export const TABLE_ROW_HEIGHT = 40;

export const TABLE_HEADER_HEIGHT = 44;

/** Beyond this the viewport stops growing and scrolls internally */
export const MAX_TABLE_HEIGHT = 700;

/** The most rows that fit under the cap before the viewport has to start scrolling */
export const MAX_VIEWPORT_ROWS = Math.floor((MAX_TABLE_HEIGHT - TABLE_HEADER_HEIGHT) / TABLE_ROW_HEIGHT);

export type TableSizing = {
	/** Passed to Table so AG Grid hugs its rows instead of opening a scroll viewport */
	autoHeight: boolean;
	/** Explicit wrapper height, or undefined to let the grid size itself */
	height: number | undefined;
};

/**
 * How to size a table currently showing `displayedRowCount` rows.
 *
 * An empty table is sized the same way as a short one so that a filter matching nothing leaves the
 * grid to show its own empty state, rather than stretching to a percentage of a wrapper that has
 * no height of its own.
 */
export const getTableSizing = (displayedRowCount: number): TableSizing =>
	displayedRowCount > MAX_VIEWPORT_ROWS
		? { autoHeight: false, height: MAX_TABLE_HEIGHT }
		: { autoHeight: true, height: undefined };
